import React, { useCallback, useEffect, useState } from "react";
import { FaPlug } from "react-icons/fa";
import { apihubGet, apihubPost } from "../../api/apihub";
import { useProjectPhone } from "../../hooks/useProjectPhone";

const WEBHOOK_EVENTS = [
  { label: "All", value: "all" },
  { label: "Customer Create", value: "customer_create" },
  { label: "Live Chat", value: "livechat" },
  { label: "Campaign Info", value: "campaign_info" },
  { label: "Template Create", value: "templatecreate" },
  { label: "Agent Create", value: "agentcreate" },
  { label: "SMS Template", value: "smstemplate" },
  { label: "SMS Deliver report", value: "smsdeliverreport" },
  { label: "Livechat transfer report", value: "livechattransferreport" },
  { label: "Livechat intervened report", value: "livechatintervenedreport" },
  { label: "Livechat resolved report", value: "livechatresolvedreport" },
  { label: "Livechat takeover report", value: "livechattakeoverreport" },
];

const ALL_EVENT = "all";

const generateSecret = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

const WebhookSubscribe = () => {
  const { projectId } = useProjectPhone();
  const [sharedSecret, setSharedSecret] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [selectedActions, setSelectedActions] = useState([]);
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const allSelected = selectedActions.includes(ALL_EVENT);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const res = await apihubGet(
        `/getInteractedCustomerDetail/projects/${projectId}`,
      );
      const project = res?.project || res?.data?.project || {};
      setWebhookUrl(project.webhookurl || "");
      setSharedSecret(project.sharedsecretkey || "");
      setSelectedActions(
        Array.isArray(project.webhookevent) ? project.webhookevent : [],
      );
      setWebhookEnabled(Boolean(project.webhookenabled));
    } catch (err) {
      setError(err.message || "Failed to load webhook settings");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const onToggleEnabled = async () => {
    if (!projectId) return;
    const next = !webhookEnabled;
    setWebhookEnabled(next);
    setToggling(true);
    setError("");
    setMessage("");
    try {
      const res = await apihubPost(
        "/getInteractedCustomerDetail/webhookenabled",
        {
          projectid: projectId,
          webhookstatus: next,
        },
      );
      if (res?.message) setMessage(res.message);
    } catch (err) {
      setWebhookEnabled(!next);
      setError(err.message || "Failed to update webhook status");
    } finally {
      setToggling(false);
    }
  };

  const onToggleEvent = (value) => {
    setSelectedActions((prev) => {
      if (value === ALL_EVENT) {
        return prev.includes(ALL_EVENT) ? [] : [ALL_EVENT];
      }
      const withoutAll = prev.filter((v) => v !== ALL_EVENT);
      if (withoutAll.includes(value)) {
        return withoutAll.filter((v) => v !== value);
      }
      return [...withoutAll, value];
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!projectId) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const res = await apihubPost("/getInteractedCustomerDetail/webhookhit", {
        projectId,
        webhookurl: webhookUrl,
        sharedsecret: sharedSecret,
        selectedActions,
      });
      if (res?.success !== false) {
        setMessage(res?.message || "Webhook settings saved successfully!");
        load();
      } else {
        setError(res?.message || "Failed to save webhook settings");
      }
    } catch (err) {
      setError(err.message || "Failed to save webhook settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="biz-panel-page">
        <p className="biz-table-message">Loading webhook settings…</p>
      </div>
    );
  }

  return (
    <div className="biz-panel-page biz-webhook-page">
      <div className="biz-webhook-top">
        <div className="biz-webhook-top-text">
          <h1>
            <FaPlug />
            Webhook Subscribe
          </h1>
          <p className="biz-panel-subtitle">
            Connect WhatsApp with your CRM and systems using real-time webhook
            automation.
          </p>
        </div>
        <div className="biz-webhook-toggle-wrap">
          <span className="biz-webhook-toggle-label">Webhook</span>
          <button
            type="button"
            className={`biz-webhook-switch ${webhookEnabled ? "is-on" : ""}`}
            onClick={onToggleEnabled}
            disabled={toggling}
            aria-pressed={webhookEnabled}
            aria-label="Toggle webhook"
          >
            <span className="biz-webhook-switch-thumb" />
          </button>
          <span
            className={`biz-webhook-status-pill ${
              webhookEnabled ? "is-on" : ""
            }`}
          >
            {toggling ? "Saving…" : webhookEnabled ? "Enabled" : "Disabled"}
          </span>
        </div>
      </div>

      {error && <p className="biz-form-error">{error}</p>}
      {message && <p className="biz-form-success">{message}</p>}

      {!webhookEnabled && (
        <div className="biz-webhook-disabled-banner">
          Webhook is currently <strong>disabled</strong>. Enable it using the
          toggle above to configure URL, secret, and events.
        </div>
      )}

      {webhookEnabled && (
        <div className="biz-webhook-form-shell">
          <form className="biz-webhook-form-card" onSubmit={onSubmit}>
            <label className="biz-form-field">
              <span>Webhook URL</span>
              <input
                id="webhookurl"
                type="text"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://yourdomain.com/endpoint"
                required
              />
            </label>

            <label className="biz-form-field">
              <span>Shared Secret Key</span>
              <div className="biz-webhook-secret-row">
                <input
                  id="sharedsecret"
                  type="text"
                  value={sharedSecret}
                  onChange={(e) => setSharedSecret(e.target.value)}
                  placeholder="secret key"
                  required
                />
                <button
                  type="button"
                  className="biz-btn-primary biz-webhook-generate-btn"
                  onClick={() => setSharedSecret(generateSecret())}
                >
                  Generate Key
                </button>
              </div>
            </label>

            <div className="biz-webhook-events-box">
              <h3>Select Webhook Events</h3>
              <div className="biz-webhook-events-list">
                {WEBHOOK_EVENTS.map(({ label, value }) => {
                  const disabled = allSelected && value !== ALL_EVENT;
                  const checked =
                    value === ALL_EVENT
                      ? allSelected
                      : selectedActions.includes(value);
                  return (
                    <label
                      key={value}
                      className={`biz-webhook-event-item ${
                        disabled ? "is-disabled" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        value={value}
                        checked={checked}
                        disabled={disabled}
                        onChange={() => onToggleEvent(value)}
                      />
                      <span>{label}</span>
                    </label>
                  );
                })}
              </div>
              <p className="biz-webhook-selected-summary">
                Selected: {selectedActions.join(", ") || "None"}
              </p>
            </div>

            <button
              type="submit"
              className="biz-btn-primary biz-webhook-subscribe-btn"
              disabled={saving || toggling}
            >
              {saving ? "Saving…" : "Subscribe"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default WebhookSubscribe;
